
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, Calendar } from 'lucide-react';
import { SubscriptionData } from '@/hooks/useSubscriptionData';

interface CurrentPlanProps {
  subscription: SubscriptionData | null;
  isLoading: boolean;
  onManagePaymentMethod: () => void;
  onCancelSubscription: () => void;
}

const CurrentPlan: React.FC<CurrentPlanProps> = ({ 
  subscription, 
  isLoading,
  onManagePaymentMethod,
  onCancelSubscription
}) => {
  // Only show cancel button if subscription is active
  const showCancelButton = subscription && subscription.status === 'active';
  
  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Current Plan
        </CardTitle>
        <CardDescription>Your current subscription details</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : !subscription ? (
          <div className="text-center py-6 text-muted-foreground">
            <p>You don't have an active subscription.</p>
            <Button className="mt-4" asChild>
              <a href="/pricing">View Plans</a>
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Plan</h3>
                <p className="font-medium">{subscription.plan}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Status</h3>
                <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  subscription.status === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {subscription.status === 'active' ? 'Active' : subscription.status === 'canceled' ? 'Canceled' : subscription.status}
                </div>
              </div>
              {subscription.status === 'active' && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Next billing date</h3>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {subscription.nextBillingDate}
                  </p>
                </div>
              )}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Amount</h3>
                <p className="font-medium">{subscription.amount}</p>
              </div>
            </div>
            
            <div className="pt-4 border-t border-border flex flex-col sm:flex-row gap-4">
              {subscription.status === 'active' && (
                <>
                  <Button 
                    variant="link" 
                    onClick={onManagePaymentMethod} 
                    className="p-0 h-auto text-primary hover:text-primary/80"
                  >
                    Update Payment Method
                  </Button>
                  <Button 
                    variant="link" 
                    onClick={onCancelSubscription} 
                    className="p-0 h-auto text-destructive hover:text-destructive/80"
                  >
                    Cancel Subscription
                  </Button>
                </>
              )}
              {subscription.status === 'canceled' && (
                <Button className="w-full sm:w-auto" asChild>
                  <a href="/pricing">Resubscribe</a>
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CurrentPlan;
